import clsx from "clsx";
import { TriangleAlert } from "lucide-react";
import React from "react";

import { IgTextField, IgTypography } from "@intelligems/ig-theme";
import { OrganizationListDto } from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useSwitchOrganization } from "@src/api/organizations/switch-organization";
import { VirtualAutoComplete } from "@src/components/VirtualAutoComplete/VirtualAutoComplete";
import { getAuthToken } from "@src/utils";

interface OrgListProps {
  options: OrganizationListDto[];
}

const Formatter = (data: OrganizationListDto) => {
  const theme = useTheme();
  return (
    <Grid container columnSpacing={1} alignItems={"center"}>
      <Grid item>
        <IgTypography>{data.name}</IgTypography>
      </Grid>
      {!data.isInstalled ? (
        <Grid item>
          <TriangleAlert color={theme.igIconColors.warning} size={16} />
        </Grid>
      ) : null}
    </Grid>
  );
};

export const OrgList = ({ options }: OrgListProps) => {
  const organization = useGetOrg();
  const switchOrganization = useSwitchOrganization();

  const handleSwitchOrg = async (orgId: string) => {
    switchOrganization.mutate({
      token: getAuthToken() || "",
      orgId: orgId,
    });
  };

  return (
    <Grid item>
      <VirtualAutoComplete
        onChange={(_ev, newValue: OrganizationListDto) => {
          void handleSwitchOrg(newValue.id);
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disableClearable={true}
        componentsProps={{
          paper: {
            sx: {
              "&.MuiPaper-root": {
                width: "275px",
              },
            },
          },
          popper: { sx: { zIndex: 1400 } },
        }}
        sx={{ zIndex: "1400 !important" }}
        value={{
          id: organization.data.org.id,
          name: organization.data.org.name,
          isInstalled: organization.data["isInstalled"] || true,
          shopId: organization.data.org.shopId!,
          createdAtTs: organization.data.org.createdAtTs || 0,
          roles: [],
        }}
        options={options}
        getOptionLabel={(option: OrganizationListDto) => option.name}
        formatter={Formatter}
        renderInput={(params) => (
          <IgTextField
            {...params}
            fullWidth={true}
            className={clsx("OrgPicker-orgName", "OrgPicker-textfield")}
            variant={"outlined"}
          />
        )}
        filterOptions={(options, state) => {
          return options.filter((option) => {
            const matchesName = option.name
              .toLowerCase()
              .includes(state.inputValue.toLowerCase());
            const matchesId = option.id
              .toLowerCase()
              .includes(state.inputValue.toLowerCase());

            const matchesShopId = option.shopId
              ?.toLowerCase()
              .includes(state.inputValue.toLowerCase());

            if (state.inputValue.length <= 4) {
              return matchesName;
            }
            return matchesName || matchesId || matchesShopId;
          });
        }}
      />
    </Grid>
  );
};
